import {HubOutlined} from '@mui/icons-material';
import {FC, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {TabPanel} from 'src/components/common/TabPanel';
import {FormattedMessage, useIntl} from 'react-intl';
import {Box, Tab, Tabs, TabsProps, Typography} from '@mui/material';
import {tagsApi} from 'src/api';
import {panelBadgeSx, panelHeaderSx, panelTitleSx, tabSelectedSx, tabsBandProps, tabsBandSx, tabsRootSx, tabSx} from './EmployeeInformation.sx';
import {RfidTable} from 'src/components/common/RfidTable';
import {EmployeeInformationProps} from './EmployeeInformation.types';
import {BexioAccountsTable} from 'src/components/common/BexioAccountsTable';

export const EmployeeInformation: FC<EmployeeInformationProps> = ({
                                                                      employeeData,
                                                                      businessId,
                                                                      companyId,
                                                                      refetchEmployee
                                                                  }) => {
    const intl = useIntl();
    const canSearchRfidTags = useCan('rfidTags.search');
    const persona = usePersona();



    // Only fetch tags if the user has permission to read RFID
    const canReadRfid = useCan('rfidTags.search');

    // The permission the endpoints behind this tab check, not a list of who is allowed to hold it.
    // Written as "back office, but not the platform administrator", it hid the tab from the one
    // account that can read every Bexio account there is.
    const canManageBexioAccounts = useCan('bexioAccounts.search');
    // Calculate tab indices dynamically based on permissions
    let tabIndex = 0;
    const rfidTabIndex = canReadRfid ? tabIndex++ : -1;
    const bexioTabIndex = canManageBexioAccounts ? tabIndex++ : -1;

    // Get RFID tags for employees
    const {data: rfidData} = tagsApi.useGetTagsQuery({
        params: {
            page: 0,
            size: 100,
        },
        data: {
            employeeId: employeeData?.id,
            businessId: businessId,
            companyId: companyId,
            sortAsc: false,
            sortField: 'CREATED_AT',
            keyword: '',
        },
    }, {
        skip: !canReadRfid || !employeeData?.id, // Skip API call if the user doesn't have permission or no employee ID
    });

    const [activeTab, setActiveTab] = useState<number>(0);

    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box sx={tabsRootSx}>
            <Box sx={panelHeaderSx}>
                <Box sx={panelBadgeSx}>
                    <HubOutlined/>
                </Box>
                <Typography sx={panelTitleSx}>
                    <FormattedMessage id="relationships"/>
                </Typography>
            </Box>
            <Box>
                <Box sx={tabsBandSx}>
                    <Tabs
                        {...tabsBandProps}
                        value={activeTab}
                        onChange={handleSetTab}
                        aria-label="employee tabs"
                    >
                        {canReadRfid && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'rfidsTabHeader'})}
                            />
                        )}
                        {canManageBexioAccounts && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'bexioAccounts'})}
                            />
                        )}
                    </Tabs>
                </Box>

                {/* RFIDS TAB */}
                {canSearchRfidTags && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={rfidTabIndex}
                    >
                        <RfidTable
                            data={employeeData?.user ? {
                                ...employeeData.user,
                                business: {
                                    id: businessId,
                                    name: '',
                                    shareClients: false
                                },
                                ocppTags: rfidData?.content || [],
                                employeeId: employeeData.id
                            } as any : undefined}
                            isCompanyUser={false}
                            companyId={companyId}
                        />
                    </TabPanel>
                                )}

                {/* BEXIO ACCOUNTS TAB */}
                {canManageBexioAccounts && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={bexioTabIndex}
                    >
                        <BexioAccountsTable
                            data={employeeData?.user}
                            refetchEmployee={refetchEmployee}
                        />
                    </TabPanel>
                )}
            </Box>
        </Box>
    );
};